import React from 'react';
import {useShallow} from 'zustand/react/shallow';
import {useReportStore} from '../../../useReportStore';
import {searchUnified} from './sourcesApi';
import {
    useSourcesExplorerActions,
    useSourcesExplorerDataState,
} from './useSourcesSectionStoreSelectors';

const SEARCH_DEBOUNCE_MS = 350;

// Fetch a larger candidate batch once per query; the results list reveals these
// progressively on scroll (client-side "endless scroll") since the unified API
// returns a single non-paginated batch.
const SEARCH_FETCH_LIMIT = 100;

/** Map the All/Corpus/My Data/Reports scope to the unified API `sources` parameter. */
function scopeToSources(unifiedSourceScope) {
    switch (unifiedSourceScope) {
        case 'corpus':
            return 'corpus';
        case 'data_items':
            return 'data_items';
        case 'reports':
            return 'reports';
        case 'highlights':
            return 'highlights';
        case 'all':
            return 'all';
        default:
            return 'both';
    }
}

function useUnifiedSearchFilters() {
    return useReportStore(
        useShallow((state) => ({
            unifiedSourceScope: state.unifiedSourceScope,
            unifiedSearchMode: state.unifiedSearchMode,
            searchPassageContext: state.searchPassageContext,
            docSource: state.docSource,
            subTypes: state.subTypes,
            tags: state.tags,
            classificationLevels: state.classificationLevels,
            productTypes: state.productTypes,
            setSearchId: state.setSearchId,
        }))
    );
}

/**
 * Drives federated unified search for the Sources panel (M6).
 *
 * Reads the debounced query from {@link useSourcesExplorerStore} and the filters from
 * {@link useReportStore}, calls {@link searchUnified}, and writes ranked, provenance-bearing
 * hits back into the explorer store. Aborts stale requests so the latest query wins.
 *
 * @param {{userId: (string|null), enabled: boolean}} params
 */
export default function useUnifiedSourcesSearch({userId, enabled = true}) {
    const {debouncedQuery} = useSourcesExplorerDataState();
    const {setSearchResults, setIsSearching, setSearchError, clearSearch} = useSourcesExplorerActions();
    const {
        unifiedSourceScope,
        unifiedSearchMode,
        searchPassageContext,
        docSource,
        subTypes,
        tags,
        classificationLevels,
        productTypes,
        setSearchId,
    } = useUnifiedSearchFilters();

    const abortRef = React.useRef(null);

    React.useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        const query = (debouncedQuery || '').trim();
        // Filter-browse mode: an empty query still runs the search when a tag filter is active,
        // returning every item labeled with the selected tags (backend keyword arms fall back to
        // filter-only recall). Empty query with no tags keeps the existing clear behavior.
        const hasTagFilter = Array.isArray(tags) && tags.length > 0;
        if (!query && !hasTagFilter) {
            if (abortRef.current) {
                abortRef.current.abort();
                abortRef.current = null;
            }
            clearSearch();
            if (setSearchId) {
                setSearchId(null);
            }
            return undefined;
        }

        const controller = new AbortController();
        if (abortRef.current) {
            abortRef.current.abort();
        }
        abortRef.current = controller;

        let cancelled = false;
        setIsSearching(true);
        setSearchError(null);

        const documentTypes = subTypes && subTypes.length ? subTypes : undefined;
        // Passage context sharpens the AI/vector ranking arm, which both Hybrid and AI use.
        const context = searchPassageContext ? searchPassageContext : undefined;
        // Uploaded / All: federate my + shared + submitted CMs (Sources folders own the 3-way split).
        const includeUploadedBranch = unifiedSourceScope === 'data_items' || unifiedSourceScope === 'all';

        searchUnified({
            q: query,
            userId,
            sources: scopeToSources(unifiedSourceScope),
            mode: unifiedSearchMode || 'keyword',
            context,
            scope: 'all',
            uploadedBranch: includeUploadedBranch ? 'all' : undefined,
            documentTypes,
            docSource: docSource || undefined,
            tags,
            classificationLevels,
            productTypes: productTypes && productTypes.length
                ? productTypes
                : undefined,
            limit: SEARCH_FETCH_LIMIT,
            signal: controller.signal,
        })
            .then((result) => {
                if (cancelled || controller.signal.aborted) {
                    return;
                }
                setSearchResults(result.items || [], result.meta || null);
                if (setSearchId) {
                    setSearchId(result.searchId || null);
                }
            })
            .catch((error) => {
                if (cancelled || controller.signal.aborted || error?.name === 'AbortError') {
                    return;
                }
                setSearchError(error?.message || 'Search failed');
                setSearchResults([], null);
                if (setSearchId) {
                    setSearchId(null);
                }
            })
            .finally(() => {
                if (!cancelled && !controller.signal.aborted) {
                    setIsSearching(false);
                }
            });

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [
        enabled,
        debouncedQuery,
        userId,
        unifiedSourceScope,
        unifiedSearchMode,
        searchPassageContext,
        docSource,
        subTypes,
        tags,
        classificationLevels,
        productTypes,
        setSearchResults,
        setIsSearching,
        setSearchError,
        clearSearch,
        setSearchId,
    ]);

    React.useEffect(() => () => {
        if (abortRef.current) {
            abortRef.current.abort();
        }
    }, []);
}

export {SEARCH_DEBOUNCE_MS, SEARCH_FETCH_LIMIT, scopeToSources};
